import { startCase } from 'es-toolkit';
import { upperFirstEveryWord } from '@zg-rentals/web-base';
import PropertyConstants from '../constants/PropertyConstants';

import numberUtils from './numberUtils';
import stringHelper from './stringHelper';

// Special chars (excluding underscore) stripped from the PMR results URL address
// segment. Must match rental-growth-web's encodeFullAddressForUrl so the address
// decodes back correctly on the price-my-rental results page.
const PMR_ADDRESS_SPECIAL_CHARS = /[!"#$%&'()*+,-./:;<=>?@[\]^`{|}~]/g;

import type {
  AccountViewListingGroupType,
  GetUserPropertyDetailsAmenitiesGroupWebDto,
  GetUserPropertyDetailsFloorPlanWebDto,
  GetUserPropertyDetailsListingTypeWebDto,
  GetUserPropertyDetailsPetsAllowedWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const propertyUtils = {
  formatAddress(
    addressComponents: Partial<{
      street: string;
      city: string;
      state: string;
      unit: string;
      name: string;
      zip: string;
    }>,
  ) {
    const { street, city, state, unit, name, zip } = addressComponents;

    // Transformations
    let formattedStreet = street ? upperFirstEveryWord(street) : '';
    const formattedCity = city ? startCase(city) : '';
    const formattedState = state ? state.toUpperCase() : '';
    const formattedUnit = unit ? `#${unit.toUpperCase()}` : '';
    const formattedName = name ? upperFirstEveryWord(name) : '';

    const formattedAddressWithoutUnit = [formattedStreet, formattedCity, `${formattedState} ${zip}`]
      .filter((val) => val)
      .join(', ');

    if (unit && unit.length) {
      formattedStreet += ` ${formattedUnit}`;
    }

    const formattedAddress = [formattedStreet, formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityState = [formattedCity, formattedState].filter((val) => val).join(', ');
    const formattedCityStateZip = `${formattedCityState} ${zip}`;
    const formattedCompleteAddress = [`${formattedStreet},`, formattedCityState, zip].filter((val) => val).join(' ');

    // NOTE: formattedKebobCase is mainly used to build /price-my-rental/results/ URLs.
    // Encode the address path segment the same way rental-growth-web's
    // encodeFullAddressForUrl does: commas become underscores (the delimiter the
    // results page decodes back to commas), other special chars are stripped, and
    // spaces become hyphens. Callers append the trailing slash and ?unit= param.
    const formattedKebobCase = encodeURIComponent(
      formattedAddressWithoutUnit
        .replaceAll(',', '_')
        .replace(PMR_ADDRESS_SPECIAL_CHARS, '')
        .replaceAll(' ', '-')
        .toLowerCase(),
    );

    return {
      formattedAddressWithoutUnit,
      formattedAddress,
      formattedCityState,
      formattedCityStateZip,
      formattedCompleteAddress,
      formattedName,
      formattedStreet,
      formattedKebobCase,
    };
  },
  isRoomForRent({ listing }: { listing?: { listingType?: GetUserPropertyDetailsListingTypeWebDto } }) {
    return listing?.listingType?.code === PropertyConstants.LISTING_CODES_BY_TYPE[PropertyConstants.LISTING_TYPES.ROOM];
  },
  formatFloorPlanDisplayPrice(lowPrice: number, highPrice: number) {
    const formattedLowPrice = numberUtils.asCurrencyWithDefault(lowPrice, '', '$0,0');
    const formattedHighPrice = numberUtils.asCurrencyWithDefault(highPrice, '', '$0,0');

    switch (true) {
      case !lowPrice && !highPrice:
        return '$ -';
      case lowPrice === highPrice:
        return `${formattedLowPrice}`;
      case Boolean(lowPrice && highPrice):
        return `${formattedLowPrice}-${formattedHighPrice}`;
      default:
        return `${formattedLowPrice || formattedHighPrice}`;
    }
  },
  getActiveFloorPlanOrUnitCount(floorPlans: Array<GetUserPropertyDetailsFloorPlanWebDto>) {
    let activeCount = 0;

    floorPlans.forEach((floorPlan) => {
      floorPlan.units &&
        floorPlan.units.forEach((unit) => {
          if (unit.active) {
            activeCount += 1;
          }
        });

      if (floorPlan?.units?.length === 0 && floorPlan.active) {
        activeCount += 1;
      }
    });

    return activeCount;
  },
  getFloorPlanDescription({
    numBathrooms,
    numBedrooms,
    lowPrice,
    highPrice,
    squareFeet,
    units,
  }: Partial<GetUserPropertyDetailsFloorPlanWebDto>) {
    const activeUnits = units
      ? units.reduce((accumulator: Array<{ lowPrice: number; highPrice: number }>, unit) => {
          if (unit.active) {
            accumulator.push({ lowPrice: unit.lowPrice, highPrice: unit.highPrice });
          }
          return accumulator;
        }, [])
      : [];

    const numActive = activeUnits.length;
    const lowActivePrice = numActive && Math.min(...activeUnits.map((unit) => unit.lowPrice));
    const highActivePrice = numActive && Math.max(...activeUnits.map((unit) => unit.highPrice));

    const displayPrice = this.formatFloorPlanDisplayPrice(
      lowActivePrice || Number(lowPrice),
      highActivePrice || Number(highPrice),
    );
    const displayTextForBaths = !numBathrooms ? '- baths' : `${stringHelper.pluralize(numBathrooms, 'bath', 'baths')}`;
    const displaySqft = squareFeet ? ` | ${squareFeet} sqft` : '';
    const displayBeds = numBedrooms || numBedrooms === 0 ? Number(numBedrooms) : '-';
    const displayTextForBeds = displayBeds === 0 ? 'studio' : `${stringHelper.pluralize(displayBeds, 'bed', 'beds')}`;

    return {
      displayPrice,
      fullDisplayText: `${displayPrice} | ${displayTextForBeds} | ${displayTextForBaths}${displaySqft}`,
    };
  },
  getIsPetsAllowedEmpty(petsAllowed?: Partial<GetUserPropertyDetailsPetsAllowedWebDto>) {
    const { negotiable: _, ...petsAllowedDtos } = petsAllowed ?? {};
    return !Object.values(petsAllowedDtos).some((petsAllowedValue) =>
      typeof petsAllowedValue === 'boolean' ? petsAllowedValue !== false : petsAllowedValue?.allowed !== null,
    );
  },
  checkIsAmenityFieldFulfilled: ({
    amenityField,
  }: {
    amenityField?: Array<GetUserPropertyDetailsAmenitiesGroupWebDto>;
  }) => {
    const requiredArray = amenityField?.filter(({ isRequired }) => isRequired);

    if (!requiredArray?.length) {
      return true;
    }

    return Boolean(
      requiredArray?.some(({ values }) => {
        let fulfilled = false;

        values.forEach(({ isSelected }) => {
          fulfilled = fulfilled || isSelected;
        });

        return fulfilled;
      }),
    );
  },

  formatUnitInfo(bedrooms?: number | null, bathrooms?: number | null, squareFeet?: number | null) {
    return `${bedrooms ?? '—'} bd • ${bathrooms ?? '—'} ba • ${squareFeet ?? '—'} sqft`;
  },

  getPropertyTypeLabel({ type, propertyType }: { type?: AccountViewListingGroupType; propertyType?: string }) {
    const { PROPERTY_TYPES, PROPERTY_TYPES_BY_ENUM } = PropertyConstants;
    if (type === 'MULTIPLEX') {
      const k = type.trim();
      return Object.hasOwn(PROPERTY_TYPES, k) ? PROPERTY_TYPES[k as keyof typeof PROPERTY_TYPES] : undefined;
    }
    if (type === 'LISTING') {
      const k = propertyType?.trim();
      return k && Object.hasOwn(PROPERTY_TYPES_BY_ENUM, k) ? PROPERTY_TYPES_BY_ENUM[k] : undefined;
    }
    return undefined;
  },
};

export default propertyUtils;
